import { useDispatch } from 'react-redux';
import { setRedirectPath } from '../Redux/Slice/authRedirectSlice';

// Login action scenarios configuration
export const LOGIN_SCENARIOS = {
  // Header login button - stay on same page
  HEADER_LOGIN: {
    id: 'header_login',
    description: 'Login from header button',
    action: 'stay_on_page',
    redirectPath: null,
    postLoginAction: null
  },
  
  // Service details page - Post inquiry Get in Touch button (first button)
  SERVICE_DETAILS_POST_INQUIRY: {
    id: 'service_details_post_inquiry',
    description: 'Login from service details post inquiry Get in Touch button',
    action: 'stay_on_page_and_open_popup',
    redirectPath: null,
    postLoginAction: 'open_inquiry_form',
    popupType: 'inquiry_form'
  },
  
  // Service details page - Profile inquiry Get in Touch button (second button)
  SERVICE_DETAILS_PROFILE_INQUIRY: {
    id: 'service_details_profile_inquiry',
    description: 'Login from service details profile inquiry Get in Touch button',
    action: 'stay_on_page_and_open_popup',
    redirectPath: null,
    postLoginAction: 'open_inquiry_form',
    popupType: 'inquiry_form'
  },
  
  // Quote request button
  QUOTE_REQUEST: {
    id: 'quote_request',
    description: 'Login from Get free Quotes button',
    action: 'stay_on_page_and_open_popup',
    redirectPath: null,
    postLoginAction: 'open_quote_request_form',
    popupType: 'quote_request_form'
  },
  
  // Profile page - Get in Touch button
  PROFILE_GET_IN_TOUCH: {
    id: 'profile_get_in_touch',
    description: 'Login from profile page Get in Touch button',
    action: 'stay_on_page_and_open_popup',
    redirectPath: null,
    postLoginAction: 'open_inquiry_form',
    popupType: 'inquiry_form'
  },
  
  // Service details page - Post report button
  SERVICE_DETAILS_POST_REPORT: {
    id: 'service_details_post_report',
    description: 'Login from service details post report button',
    action: 'stay_on_page_and_open_popup',
    redirectPath: null,
    postLoginAction: 'open_post_report_form',
    popupType: 'post_report_form'
  },
  
  // Service details page - Profile report button
  SERVICE_DETAILS_PROFILE_REPORT: {
    id: 'service_details_profile_report',
    description: 'Login from service details profile report button',
    action: 'stay_on_page_and_open_popup',
    redirectPath: null,
    postLoginAction: 'open_profile_report_form',
    popupType: 'profile_report_form'
  },
  
  // Profile page - Report button
  PROFILE_REPORT: {
    id: 'profile_report',
    description: 'Login from profile page report button',
    action: 'stay_on_page_and_open_popup',
    redirectPath: null,
    postLoginAction: 'open_profile_report_form',
    popupType: 'profile_report_form'
  },
  
  // Profile page - Write Review button
  PROFILE_WRITE_REVIEW: {
    id: 'profile_write_review',
    description: 'Login from profile page write review button',
    action: 'stay_on_page_and_open_popup',
    redirectPath: null,
    postLoginAction: 'open_review_form',
    popupType: 'review_form'
  }
};

/**
 * Login Action Service
 * Handles different login scenarios with specific post-login actions
 */
export class LoginActionService {
  constructor() {
    this.scenarios = LOGIN_SCENARIOS;
  }

  /**
   * Get scenario by ID
   * @param {string} scenarioId - The scenario ID
   * @returns {Object|null} - The scenario configuration or null if not found
   */
  getScenario(scenarioId) {
    // Find scenario by ID in the scenarios object
    for (const key in this.scenarios) {
      if (this.scenarios[key].id === scenarioId) {
        return this.scenarios[key];
      }
    }
    return null;
  }

  /**
   * Handle login trigger for a specific scenario
   * @param {string} scenarioId - The scenario ID
   * @param {Object} options - Additional options
   * @param {Function} options.setSignIn - Function to open login modal
   * @param {Function} options.dispatch - Redux dispatch function
   * @param {string} options.currentPath - Current page path
   * @param {Object} options.auth - Current auth state
   * @param {Function} options.setPopupOpen - Function to open specific popup
   * @param {Object} options.popupData - Data to pass to popup
   */
  handleLoginTrigger(scenarioId, options = {}) {
    const {
      setSignIn,
      dispatch,
      currentPath,
      auth,
      setPopupOpen,
      popupData = {}
    } = options;

    const scenario = this.getScenario(scenarioId);
    
    if (!scenario) {
      console.error(`Unknown login scenario: ${scenarioId}`);
      return;
    }

    // If user is already logged in, handle the action directly
    if (auth?.user) {
      this.handlePostLoginAction(scenario, { setPopupOpen, popupData });
      return;
    }

    // User is not logged in, trigger login with scenario configuration
    if (dispatch && currentPath) {
      dispatch(setRedirectPath(currentPath));
    }

    if (setSignIn) {
      setSignIn(true);
    }

    // Store scenario info for post-login handling
    this.storeScenarioForPostLogin(scenarioId, scenario, popupData);
  }

  /**
   * Handle post-login actions
   * @param {Object} scenario - The scenario configuration
   * @param {Object} options - Options for post-login actions
   */
  handlePostLoginAction(scenario, options = {}) {
    const { setPopupOpen, popupData = {} } = options;
    
    console.log('handlePostLoginAction called with scenario:', scenario);
    console.log('setPopupOpen function:', setPopupOpen);

    switch (scenario.postLoginAction) {
      case 'open_inquiry_form':
        console.log('Opening inquiry form');
        if (setPopupOpen) {
          // Check if setPopupOpen is the new handlePopupOpen function (expects popupType, data)
          if (typeof setPopupOpen === 'function' && setPopupOpen.length >= 1) {
            // New function signature: handlePopupOpen(popupType, data)
            setPopupOpen('inquiry_form', popupData);
            console.log('Inquiry form opened with popupType: inquiry_form and data:', popupData);
          } else {
            // Old function signature: setPopupOpen(true)
            setPopupOpen(true);
            console.log('Inquiry form opened with default action');
          }
        } else {
          console.log('setPopupOpen function not provided for inquiry form');
        }
        break;
        
      case 'open_quote_request_form':
        console.log('Opening quote request form');
        if (setPopupOpen) {
          // Check if setPopupOpen is the new handlePopupOpen function
          if (typeof setPopupOpen === 'function' && setPopupOpen.length >= 1) {
            // New function signature: handlePopupOpen(popupType, data)
            setTimeout(() => {
              setPopupOpen('quote_request_form', popupData);
              console.log('Quote request form opened');
            }, 50);
          } else {
            // Old function signature: setPopupOpen(true)
            setTimeout(() => {
              setPopupOpen(true);
              console.log('Quote request form opened');
            }, 50);
          }
        } else {
          console.log('setPopupOpen function not provided for quote request form');
        }
        break;
        
      case 'open_post_report_form':
        console.log('Opening post report form');
        if (setPopupOpen) {
          // Check if setPopupOpen is the new handlePopupOpen function
          if (typeof setPopupOpen === 'function' && setPopupOpen.length >= 1) {
            // New function signature: handlePopupOpen(popupType, data)
            setTimeout(() => {
              setPopupOpen('post_report_form', popupData);
              console.log('Post report form opened');
            }, 50);
          } else {
            // Old function signature: setPopupOpen(true)
            setTimeout(() => {
              setPopupOpen(true);
              console.log('Post report form opened');
            }, 50);
          }
        } else {
          console.log('setPopupOpen function not provided for post report form');
        }
        break;
        
      case 'open_profile_report_form':
        console.log('Opening profile report form');
        if (setPopupOpen) {
          // Check if setPopupOpen is the new handlePopupOpen function
          if (typeof setPopupOpen === 'function' && setPopupOpen.length >= 1) {
            // New function signature: handlePopupOpen(popupType, data)
            setTimeout(() => {
              setPopupOpen('profile_report_form', popupData);
              console.log('Profile report form opened');
            }, 50);
          } else {
            // Old function signature: setPopupOpen(true)
            setTimeout(() => {
              setPopupOpen(true);
              console.log('Profile report form opened');
            }, 50);
          }
        } else {
          console.log('setPopupOpen function not provided for profile report form');
        }
        break;
        
      case 'open_review_form':
        console.log('Opening review form');
        if (setPopupOpen) {
          // Check if setPopupOpen is the new handlePopupOpen function
          if (typeof setPopupOpen === 'function' && setPopupOpen.length >= 1) {
            // New function signature: handlePopupOpen(popupType, data)
            setTimeout(() => {
              setPopupOpen('review_form', popupData);
              console.log('Review form opened');
            }, 50);
          } else {
            // Old function signature: setPopupOpen(true)
            setTimeout(() => {
              setPopupOpen(true);
              console.log('Review form opened');
            }, 50);
          }
        } else {
          console.log('setPopupOpen function not provided for review form');
        }
        break;
        
      default:
        console.log('No specific action needed for scenario:', scenario.postLoginAction);
        break;
    }
  }

  /**
   * Store scenario information for post-login handling
   * @param {string} scenarioId - The scenario ID
   * @param {Object} scenario - The scenario configuration
   * @param {Object} popupData - Data to pass to popup
   */
  storeScenarioForPostLogin(scenarioId, scenario, popupData = {}) {
    const scenarioInfo = {
      scenarioId,
      scenario,
      popupData,
      timestamp: Date.now()
    };
    
    console.log('Storing scenario:', scenarioInfo);
    // Store in sessionStorage for post-login retrieval
    sessionStorage.setItem('pendingLoginScenario', JSON.stringify(scenarioInfo));
    console.log('Scenario stored in sessionStorage');
  }

  /**
   * Get stored scenario information
   * @returns {Object|null} - The stored scenario info or null
   */
  getStoredScenario() {
    const stored = sessionStorage.getItem('pendingLoginScenario');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.error('Error parsing stored scenario:', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Clear stored scenario information
   */
  clearStoredScenario() {
    sessionStorage.removeItem('pendingLoginScenario');
  }

  /**
   * Check if there's a pending scenario after login
   * @param {Function} setPopupOpen - Function to open popup
   * @returns {boolean} - True if scenario was handled
   */
  checkAndHandlePendingScenario(setPopupOpen) {
    console.log('checkAndHandlePendingScenario called');
    
    // Check if user is logged in by looking at localStorage
    const storedAuth = localStorage.getItem("LoginSession");
    if (!storedAuth) {
      console.log('No stored auth, returning false');
      return false;
    }
    
    try {
      const authData = JSON.parse(storedAuth);
      if (!authData.user) {
        console.log('No auth user in localStorage, returning false');
        return false;
      }
      console.log('Auth user found in localStorage:', authData.user);
    } catch (error) {
      console.log('Error parsing stored auth, returning false');
      return false;
    }

    const storedScenario = this.getStoredScenario();
    console.log('storedScenario:', storedScenario);
    
    if (!storedScenario) {
      console.log('No stored scenario, returning false');
      return false;
    }

    // Check if scenario is still valid (within 5 minutes)
    const timeDiff = Date.now() - storedScenario.timestamp;
    if (timeDiff > 5 * 60 * 1000) {
      console.log('Scenario expired, clearing and returning false');
      this.clearStoredScenario();
      return false;
    }

    console.log('Handling pending scenario:', storedScenario.scenario.id);
    console.log('Scenario postLoginAction:', storedScenario.scenario.postLoginAction);
    
    // Handle the pending scenario
    this.handlePostLoginAction(storedScenario.scenario, {
      setPopupOpen,
      popupData: storedScenario.popupData
    });

    // Clear the stored scenario
    this.clearStoredScenario();
    console.log('Scenario handled and cleared');
    return true;
  }
}

// Create singleton instance
export const loginActionService = new LoginActionService();

/**
 * Hook for using login action service
 */
export const useLoginActionService = () => {
  const dispatch = useDispatch();
  
  return {
    ...loginActionService,
    handleLoginTrigger: (scenarioId, options = {}) => {
      loginActionService.handleLoginTrigger(scenarioId, {
        ...options,
        dispatch
      });
    },
    checkAndHandlePendingScenario: (setPopupOpen) => {
      return loginActionService.checkAndHandlePendingScenario(setPopupOpen);
    }
  };
};
